// Stratul de desen de deasupra graficului.
//
// Desenele se țin în coordonate de piață (timp + preț), nu în pixeli — așa rămân lipite
// de lumânări la zoom și scroll. Un canvas transparent peste panoul principal le redesenează
// la fiecare schimbare de scară.

import { settings } from '../core/state.js';
import { el, fmtPrice, priceDigits, TF_SECONDS } from '../core/util.js';
import { fmtTimpCruce } from './future-axis.js';

export const TOOLS = [
  { id: 'cursor', label: 'Курсор', hotkey: 'V', icon: 'cursor' },
  { id: 'trend', label: 'Трендовая линия', hotkey: 'L', icon: 'trend', points: 2 },
  { id: 'hline', label: 'Горизонтальная линия', hotkey: 'H', icon: 'hline', points: 1 },
  { id: 'hray', label: 'Горизонтальный луч', hotkey: 'Shift+H', icon: 'hray', points: 1 },
  { id: 'vline', label: 'Вертикальная линия', hotkey: 'I', icon: 'vline', points: 1 },
  { id: 'ray', label: 'Луч', hotkey: 'Y', icon: 'ray', points: 2 },
  { id: 'arrow', label: 'Стрелка', hotkey: 'A', icon: 'arrow', points: 2 },
  { id: 'rect', label: 'Прямоугольник', hotkey: 'R', icon: 'rect', points: 2 },
  { id: 'fib', label: 'Фибоначчи', hotkey: 'F', icon: 'fib', points: 2 },
  { id: 'ruler', label: 'Линейка', hotkey: 'M', icon: 'ruler', points: 2 },
  { id: 'rr', label: 'Риск/прибыль', hotkey: 'Shift+R', icon: 'rr', points: 3 },
  { id: 'brush', label: 'Кисть', hotkey: 'B', icon: 'brush', points: 'free' },
  { id: 'text', label: 'Текст', hotkey: 'T', icon: 'text', points: 1 },
  { id: 'eraser', label: 'Ластик', hotkey: 'E', icon: 'eraser' },
];

export const TOOL_BY_ID = new Map(TOOLS.map((t) => [t.id, t]));

export const PALETTE = ['#2f81f7', '#26a69a', '#ef5350', '#f0b90b', '#b07cf0', '#e6edf3'];

export const DEFAULT_FIB_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];

const HIT_RADIUS = 6;
/** Raza mânerelor albe de la capete și a clopoțelului de alertă. */
const HANDLE_RADIUS = 6;
const BELL_RADIUS = 11;
const STORE_KEY = 'sx_chart_drawings';

/** Copie adâncă a unei liste de desene. Sunt date simple: puncte, culori, numere. */
function clona(lista) {
  return lista ? JSON.parse(JSON.stringify(lista)) : [];
}
const STYLE_KEY = 'sx_lw_draw_style';
const FIB_KEY = 'sx_lw_fib_levels';
const MAGNET_KEY = 'sx_lw_magnet_power';

/** Distanța de la punctul p la segmentul ab, în pixeli. */
function distToSegment(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

export class DrawingLayer {
  /**
   * @param {Chart} chart  instanța noastră de grafic
   * @param {HTMLElement} host  containerul panoului principal
   */
  constructor(chart, host) {
    this.chart = chart;
    this.host = host;
    this.tool = 'cursor';
    this.color = PALETTE[0];
    this.style = settings.get(STYLE_KEY, { width: 2, opacity: 1 });
    this.fibLevels = settings.get(FIB_KEY, DEFAULT_FIB_LEVELS);
    this.magnet = false;
    this.magnetPower = settings.get(MAGNET_KEY, 12);
    this.drawings = [];
    this.pending = null;       // desenul în curs de construire
    this.hover = null;
    this.cursor = null;
    this.undoStack = [];
    this.onToolDone = null;

    this.#buildCanvas();
    this.#bindEvents();
    this.load();
  }

  get storeKey() {
    return `${this.chart.exchange}:${this.chart.market}:${this.chart.symbol}`;
  }

  // ------------------------------------------------------------------ canvas

  #buildCanvas() {
    this.canvas = el('canvas', { class: 'chart-draw-layer' });
    this.ctx = this.canvas.getContext('2d');
    this.host.append(this.canvas);
    this.resizeObserver = new ResizeObserver(() => this.#resize());
    this.resizeObserver.observe(this.host);
    this.#resize();
    // `Chart` ne redesenează când se schimbă proiecția — inclusiv la scara de preț,
    // pentru care Lightweight Charts nu oferă niciun eveniment.
    this.chart.attachDrawLayer(this);
  }

  #resize() {
    const dpr = window.devicePixelRatio || 1;
    const w = this.host.clientWidth;
    const h = this.host.clientHeight;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.render();
  }

  // ---------------------------------------------------------- coordonate

  // Conversia trece prin `Chart`, care o face corect și dincolo de ultima lumânare.
  toPixel(point) { return this.chart.toPixel(point); }

  toMarket(x, y) { return this.chart.toMarket(x, y); }

  /** Cu magnetul pornit, lipește punctul de cel mai apropiat O/H/L/C. */
  snap(point) {
    if (!this.magnet || !point) return point;
    const step = TF_SECONDS[this.chart.tf] || 300;
    const target = Math.round(point.time / step) * step;
    const bar = this.chart.bars.find((b) => b.time === target);
    if (!bar) return { ...point, time: target };

    const pixel = this.toPixel(point);
    if (!pixel) return { ...point, time: target };

    let best = null;
    for (const price of [bar.open, bar.high, bar.low, bar.close]) {
      const y = this.chart.candles.priceToCoordinate(price);
      if (y === null) continue;
      const dist = Math.abs(y - pixel.y);
      if (dist <= this.magnetPower && (!best || dist < best.dist)) best = { price, dist };
    }
    return { time: target, price: best ? best.price : point.price };
  }

  // ------------------------------------------------------------ interacțiune

  #bindEvents() {
    const pos = (event) => {
      const rect = this.canvas.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    // Evenimente de **pointer**, nu de mouse: ele vin la fel de la mouse, de la deget
    // si de la creion. Cu `mousedown` singur, pe telefon nu se intampla nimic — degetul
    // nu produce apasari de mouse, iar graficul de dedesubt lua atingerea si panorama.
    // De aceea desenul pe telefon parea ca nu exista.
    this.canvas.addEventListener('pointerdown', (event) => {
      // Doar clicul stang muta/deseneaza. Rotita (masuratoarea cu butonul din mijloc) si
      // clicul drept trec neatinse mai departe: fara asta, apasarea cu rotita pe o linie o
      // apuca si o tragea, in loc sa porneasca masurarea in procente.
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      // Cu cursorul in mana, panza primeste evenimente doar cat timp exista un desen
      // ales — atunci se pot apuca capetele lui. In rest ramane transparenta, ca sa
      // poata fi mutat graficul.
      if (this.tool === 'cursor') {
        const p = pos(event);

        const capat = this.#capatLa(p);
        if (capat) {
          event.preventDefault();
          event.stopPropagation();
          try { this.canvas.setPointerCapture(event.pointerId); } catch { /* nu peste tot */ }
          this.mutaCapatul = capat;
          return;
        }
        // Clopotelul de alerta are prioritate: altfel clicul pe el ar incepe o mutare.
        const clopot = this.#bellAt(p);
        if (clopot) {
          event.preventDefault();
          event.stopPropagation();
          this.onAlertToggle?.(clopot);
          return;
        }
        // Atingere in afara: renuntam la selectie si dam graficul inapoi degetului.
        const alt = this.#hitTest(p);
        if (!alt) { this.deselect(); this.#syncPanza(); return; }
        // Apucat de oriunde pe corpul lui: desenul se muta intreg — linia orizontala doar
        // in sus si in jos, restul si in timp.
        event.preventDefault();
        event.stopPropagation();
        try { this.canvas.setPointerCapture(event.pointerId); } catch { /* nu peste tot */ }
        this.selected = alt;
        this.mutaDesen = { desen: alt, start: this.toMarket(p.x, p.y), orig: clona(alt.points), mutat: false };
        this.render();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      // Prindem pointerul: degetul poate iesi din panza in timpul tragerii, iar fara
      // captura miscarile de dupa nu mai ajung aici si linia se opreste la margine.
      try { this.canvas.setPointerCapture(event.pointerId); } catch { /* nu peste tot */ }
      const p = pos(event);
      const market = this.snap(this.toMarket(p.x, p.y));
      if (!market) return;

      if (this.tool === 'eraser') { this.#eraseAt(p); return; }
      if (this.tool === 'text') { this.#addText(market); return; }

      // Retinem de unde a plecat degetul: daca pana la ridicare s-a miscat, gestul e
      // „trage o linie" si se termina la ridicare; daca n-a plecat de pe loc, ramane
      // vechiul fel — o apasare pentru primul capat, alta pentru al doilea. Amandoua
      // trebuie sa mearga: pe telefon e firesc sa tragi, pe calculator multi apasa.
      this.plecatDin = p;
      this.trage = false;
      this.#startOrExtend(market);
    });

    this.canvas.addEventListener('pointermove', (event) => {
      const p = pos(event);
      this.cursor = p;
      if (this.tool !== 'cursor') event.preventDefault();

      // Mutarea intregului desen.
      if (this.mutaDesen) {
        const m = this.mutaDesen;
        const acum = this.toMarket(p.x, p.y);
        if (acum && m.start) {
          const dPret = acum.price - m.start.price;
          const dTimp = acum.time - m.start.time;
          const t = m.desen.tool;
          m.desen.points = m.orig.map((pt) => ({
            time: t === 'hline' ? pt.time : pt.time + dTimp,
            price: t === 'vline' ? pt.price : pt.price + dPret,
          }));
          m.mutat = true;
          this.render();
        }
        event.preventDefault();
        return;
      }

      // Mutarea unui capat al desenului ales.
      if (this.mutaCapatul) {
        const market = this.snap(this.toMarket(p.x, p.y));
        if (market) {
          this.mutaCapatul.desen.points[this.mutaCapatul.index] = market;
          this.render();
        }
        event.preventDefault();
        return;
      }

      if (this.tool === 'brush' && this.pending && event.buttons === 1) {
        const market = this.toMarket(p.x, p.y);
        if (market) this.pending.points.push(market);
      }

      // Al doilea capat se plimba dupa deget cat timp acesta apasa: asa vezi linia
      // inainte s-o lasi, nu dupa.
      if (this.pending && this.plecatDin && event.buttons === 1) {
        const def = TOOL_BY_ID.get(this.tool);
        if (def && def.points === 2) {
          if (Math.hypot(p.x - this.plecatDin.x, p.y - this.plecatDin.y) > 6) this.trage = true;
          if (this.trage) {
            const market = this.snap(this.toMarket(p.x, p.y));
            if (market) this.pending.points[1] = market;
          }
        }
      }

      if (this.tool === 'eraser' || this.tool === 'cursor') this.hover = this.#hitTest(p);
      if (this.tool === 'cursor') {
        this.canvas.style.cursor = this.#capatLa(p) ? 'grab'
          : !this.hover ? 'default'
            : this.hover.tool === 'hline' || this.hover.tool === 'hray' ? 'ns-resize'
              : this.hover.tool === 'vline' ? 'ew-resize' : 'move';
        this.#syncPanza();
      }
      this.render();
    });

    this.canvas.addEventListener('pointerup', (event) => {
      try { this.canvas.releasePointerCapture(event.pointerId); } catch { /* idem */ }

      if (this.mutaDesen) {
        const { desen, mutat } = this.mutaDesen;
        this.mutaDesen = null;
        if (mutat) {
          this.save();
          if (desen.alertId) this.onAlertMoved?.(desen);   // alerta urmează linia
        }
        this.render();
        return;
      }

      if (this.mutaCapatul) {
        const { desen } = this.mutaCapatul;
        this.mutaCapatul = null;
        this.save();
        if (desen.alertId) this.onAlertMoved?.(desen);
        this.render();
        return;
      }

      if (this.tool === 'brush' && this.pending) { this.#commit(); }
      // Degetul s-a miscat: linia e gata acolo unde a fost lasata.
      else if (this.trage && this.pending && this.pending.points.length >= 2) this.#commit();

      this.plecatDin = null;
      this.trage = false;
    });

    this.canvas.addEventListener('pointerleave', () => {
      this.cursor = null;
      this.hover = null;
      this.render();
    });

    // În modul cursor canvasul e transparent la evenimente, ca să nu blocheze
    // panoramarea graficului. De aceea evidențierea și selecția ascultă pe container.
    this.hostMove = (event) => {
      if (this.tool !== 'cursor') return;
      const p = pos(event);
      const previous = this.hover;
      this.hover = this.#bellAt(p) || this.#hitTest(p);
      this.host.style.cursor = this.hover ? 'pointer' : '';
      // Deasupra unei linii pânza preia mouse-ul: linia se apucă și se trage direct, fără
      // un clic de selecție înainte. În rest rămâne transparentă (graficul se mișcă).
      this.#syncPanza();
      if (this.hover && !this.mutaDesen) {
        this.canvas.style.cursor = this.#bellAt(p) ? 'pointer'
          : this.hover.tool === 'hline' || this.hover.tool === 'hray' ? 'ns-resize'
            : this.hover.tool === 'vline' ? 'ew-resize' : 'move';
      }
      if (previous !== this.hover) this.render();
    };

    this.hostDown = (event) => {
      // `button === 0` acopera si atingerea: un deget se raporteaza ca butonul principal.
      if (this.tool !== 'cursor' || event.button !== 0) return;
      this.pressAt = pos(event);
    };

    this.hostUp = (event) => {
      if (this.tool !== 'cursor' || event.button !== 0 || !this.pressAt) return;
      const p = pos(event);
      const moved = Math.hypot(p.x - this.pressAt.x, p.y - this.pressAt.y) > 4;
      this.pressAt = null;
      if (moved) return;                       // a fost o panoramare, nu un clic

      // Clopoțelul de pe desenul selectat armează sau dezarmează alerta.
      const bell = this.#bellAt(p);
      if (bell) { this.onAlertToggle?.(bell); return; }

      this.selected = this.#hitTest(p);
      this.#syncPanza();
      this.render();
    };

    // Dublu-clic pe o linie orizontală o transformă direct în alertă de nivel.
    this.hostDblClick = (event) => {
      const hit = this.#hitTest(pos(event));
      if (!hit || !this.#canAlert(hit)) return;
      event.preventDefault();
      event.stopPropagation();
      this.onAlertToggle?.(hit);
    };

    this.host.addEventListener('pointermove', this.hostMove);
    this.host.addEventListener('pointerdown', this.hostDown);
    this.host.addEventListener('pointerup', this.hostUp);
    this.host.addEventListener('dblclick', this.hostDblClick);

    this.canvas.addEventListener('contextmenu', (event) => {
      if (this.tool === 'cursor') return;
      event.preventDefault();
      this.cancel();
    });

    this.keyHandler = (event) => this.#handleKey(event);
    // Ctrl ținut apăsat = magnet temporar; la eliberare revenim la starea comutatorului.
    this.keyUpHandler = (event) => {
      if (event.key === 'Control' && !this.magnetLocked) { this.magnet = false; this.render(); }
    };
    window.addEventListener('keydown', this.keyHandler);
    window.addEventListener('keyup', this.keyUpHandler);
  }

  #handleKey(event) {
    // `target` poate fi `window` sau `document`, care nu au `matches` — de aceea opțional.
    if (event.target?.matches?.('input, textarea, [contenteditable]')) return;
    const key = event.key.toLowerCase();

    if (key === 'control') { this.magnet = true; this.render(); return; }
    if (event.key === 'Escape') { this.cancel(); this.deselect(); return; }
    if ((event.ctrlKey || event.metaKey) && key === 'z') { event.preventDefault(); this.undo(); return; }

    // Desenul selectat se șterge cu Delete sau Backspace, ca la ei.
    if ((event.key === 'Delete' || event.key === 'Backspace') && this.selected) {
      event.preventDefault();
      this.remove(this.selected);
      return;
    }

    // Tastele uneltelor sunt litere simple, adică exact ce ar deschide căutarea globală.
    // Le lăsăm să lucreze doar când cursorul e deasupra graficului: acolo desenezi. În
    // rest, litera înseamnă „caut o monedă". Escape, Delete și Ctrl+Z rămân globale —
    // desenul selectat trebuie să se poată șterge și după ce ai plecat cu mouse-ul.
    if (!this.host.matches(':hover')) return;

    for (const tool of TOOLS) {
      const needsShift = tool.hotkey.startsWith('Shift+');
      const letter = tool.hotkey.replace('Shift+', '').toLowerCase();
      if (key === letter && event.shiftKey === needsShift) {
        event.preventDefault();
        this.setTool(tool.id);
        return;
      }
    }
    if (key === 'g') { this.magnetLocked = !this.magnetLocked; this.setMagnet(this.magnetLocked); }
  }

  #startOrExtend(market) {
    const def = TOOL_BY_ID.get(this.tool);
    if (!def) return;

    if (!this.pending) {
      this.pending = {
        id: `d_${Date.now().toString(36)}`,
        tool: this.tool,
        points: [market],
        color: this.color,
        width: this.style.width,
        opacity: this.style.opacity,
      };
      if (this.tool === 'rr') this.pending.side = this.rrSide || 'long';
      if (def.points === 1) this.#commit();
      return;
    }

    this.pending.points.push(market);
    if (def.points !== 'free' && this.pending.points.length >= def.points) this.#commit();
  }

  #addText(market) {
    const text = window.prompt('Текст на графике:');
    if (!text) return;
    this.drawings.push({
      id: `d_${Date.now().toString(36)}`,
      tool: 'text', points: [market], text,
      color: this.color, width: this.style.width, opacity: this.style.opacity,
    });
    this.save();
    this.render();
    this.onToolDone?.();
  }

  #commit() {
    if (!this.pending) return;
    this.drawings.push(this.pending);
    this.pending = null;
    this.save();
    this.render();
    this.onToolDone?.();
  }

  cancel() {
    this.pending = null;
    this.render();
  }

  #hitTest(pixel) {
    for (let i = this.drawings.length - 1; i >= 0; i--) {
      const d = this.drawings[i];
      const pts = d.points.map((p) => this.toPixel(p)).filter(Boolean);
      if (!pts.length) continue;

      if (d.tool === 'hline' || d.tool === 'hray') {
        if (Math.abs(pixel.y - pts[0].y) <= HIT_RADIUS
            && (d.tool === 'hline' || pixel.x >= pts[0].x)) return d;
        continue;
      }
      if (d.tool === 'vline') {
        if (Math.abs(pixel.x - pts[0].x) <= HIT_RADIUS) return d;
        continue;
      }
      if (d.tool === 'text') {
        if (Math.hypot(pixel.x - pts[0].x, pixel.y - pts[0].y) <= 20) return d;
        continue;
      }
      if (d.tool === 'rect' && pts.length >= 2) {
        const [a, b] = pts;
        const inX = pixel.x >= Math.min(a.x, b.x) - HIT_RADIUS && pixel.x <= Math.max(a.x, b.x) + HIT_RADIUS;
        const inY = pixel.y >= Math.min(a.y, b.y) - HIT_RADIUS && pixel.y <= Math.max(a.y, b.y) + HIT_RADIUS;
        if (inX && inY) return d;
        continue;
      }
      for (let j = 1; j < pts.length; j++) {
        if (distToSegment(pixel, pts[j - 1], pts[j]) <= HIT_RADIUS) return d;
      }
    }
    return null;
  }

  #eraseAt(pixel) {
    const hit = this.#hitTest(pixel);
    if (hit) this.remove(hit);
  }

  /**
   * Ce forme pot purta o alertă: orice linie de nivel, inclusiv cele înclinate.
   * Pentru o trendline serverul recalculează prețul din panta dreptei, deci alerta
   * urmează linia în timp, nu rămâne la prețul din clipa armării.
   */
  #canAlert(drawing) {
    return ['hline', 'hray', 'trend', 'ray'].includes(drawing.tool);
  }

  /** Centrul clopoțelului de pe desenul selectat, în pixeli. */
  #bellCenter(drawing) {
    if (!drawing || !this.#canAlert(drawing)) return null;
    const pts = drawing.points.map((p) => this.toPixel(p));
    if (!pts[0]) return null;
    const width = this.canvas.width / (window.devicePixelRatio || 1);

    if (drawing.tool === 'hline') return { x: width / 2, y: pts[0].y };
    if (drawing.tool === 'hray') return { x: (pts[0].x + width) / 2, y: pts[0].y };
    // Liniile cu două capete: clopoțelul stă la mijlocul segmentului.
    if (!pts[1]) return null;
    return { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
  }

  /** Desenul al cărui clopoțel e sub cursor, dacă există. */
  /**
   * Capatul de desen de sub deget, daca exista.
   *
   * Raza de prindere e mai mare decat cerculetul desenat: un deget acopera vreo zece
   * milimetri, iar un punct de sase pixeli nu se poate nimeri cu el. Pe mouse diferenta
   * nu se simte, pe telefon e diferenta dintre „merge" si „nu merge".
   */
  #capatLa(pixel) {
    if (!this.selected) return null;
    const raza = HANDLE_RADIUS * 3;
    const puncte = this.selected.points;
    for (let i = 0; i < puncte.length; i++) {
      const px = this.toPixel(puncte[i]);
      if (!px) continue;
      if (Math.hypot(px.x - pixel.x, px.y - pixel.y) <= raza) {
        return { desen: this.selected, index: i };
      }
    }
    return null;
  }

  /** Panza prinde evenimente cand desenezi sau cand ai un desen ales. */
  #syncPanza() {
    const activa = this.tool !== 'cursor' || !!this.selected || !!this.hover || !!this.mutaDesen;
    this.canvas.style.pointerEvents = activa ? 'auto' : 'none';
    this.canvas.style.touchAction = activa ? 'none' : '';
  }

  #bellAt(pixel) {
    const candidates = this.selected ? [this.selected] : [];
    for (const d of candidates) {
      const c = this.#bellCenter(d);
      if (c && Math.hypot(pixel.x - c.x, pixel.y - c.y) <= BELL_RADIUS + 2) return d;
    }
    return null;
  }

  /** Șterge un desen și, dacă avea alertă armată, o anunță pe cea care o ține. */
  remove(drawing) {
    this.undoStack.push([...this.drawings]);
    this.drawings = this.drawings.filter((d) => d !== drawing);
    if (this.selected === drawing) this.selected = null;
    if (this.hover === drawing) this.hover = null;
    if (drawing.alertId) this.onAlertRemoved?.(drawing);
    this.save();
    this.render();
  }

  deselect() {
    if (!this.selected) return;
    this.selected = null;
    this.#syncPanza();
    this.render();
  }

  // ------------------------------------------------------------- comenzi

  setTool(tool) {
    this.tool = tool;
    this.pending = null;
    // Selecția are sens doar cu cursorul; la trecerea pe o unealtă o eliberăm.
    if (tool !== 'cursor') { this.selected = null; this.host.style.cursor = ''; }
    // Fara asta, degetul care traseaza o linie deruleaza si pagina in acelasi timp:
    // browserul crede ca vrei sa dai paginii drumul in jos. `none` ii spune ca gestul
    // e al nostru cat timp o unealta e in mana sau un desen e ales.
    this.#syncPanza();
    this.canvas.style.cursor = tool === 'eraser' ? 'cell' : (tool === 'cursor' ? 'default' : 'crosshair');
    this.render();
    this.onToolChange?.(tool);
  }

  setColor(color) { this.color = color; }

  setStyle(patch) {
    this.style = { ...this.style, ...patch };
    settings.set(STYLE_KEY, this.style);
  }

  setFibLevels(levels) {
    this.fibLevels = levels;
    settings.set(FIB_KEY, levels);
    this.render();
  }

  setMagnet(on) {
    this.magnet = on;
    this.magnetLocked = on;
    this.render();
    this.onMagnetChange?.(on);
  }

  setMagnetPower(power) {
    this.magnetPower = power;
    settings.set(MAGNET_KEY, power);
  }

  setRRSide(side) { this.rrSide = side; }

  undo() {
    if (this.undoStack.length) this.drawings = this.undoStack.pop();
    else this.drawings.pop();
    this.save();
    this.render();
  }

  clearAll() {
    this.undoStack.push([...this.drawings]);
    this.drawings = [];
    this.save();
    this.render();
  }

  // ------------------------------------------------------------ persistență

  /**
   * Desenele monedei curente, **copiate** din setări.
   *
   * Copia nu e prisos, e miezul. Fără ea luam chiar tabloul pe care îl ţin setările, iar
   * fiecare desen nou se adăuga direct în valoarea salvată. `settings.set` compară
   * valoarea nouă cu cea veche ca să nu trimită degeaba la server — dar cele două erau
   * acelaşi obiect, deci mereu egale: nu se trimitea nimic. Se salva doar primul desen
   * de pe o monedă, cel de dinaintea primei citiri; tot ce urma rămânea în fila aceasta
   * şi nu ajungea niciodată nici pe server, nici pe celălalt aparat.
   */
  load() {
    const all = settings.get(STORE_KEY, {}) || {};
    this.drawings = clona(all[this.storeKey]);
    this.render();
  }

  save() {
    const all = { ...(settings.get(STORE_KEY, {}) || {}) };
    if (this.drawings.length) all[this.storeKey] = clona(this.drawings);
    else delete all[this.storeKey];
    settings.set(STORE_KEY, all);
  }

  // ---------------------------------------------------------------- randare

  render() {
    const ctx = this.ctx;
    if (!ctx) return;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    for (const d of this.drawings) this.#draw(d, d === this.hover || d === this.selected);
    if (this.pending) this.#draw(this.#withCursor(this.pending), false, true);
    // Mânerele și clopoțelul se desenează peste tot, ca să rămână vizibile.
    if (this.selected) this.#drawSelection(this.selected);
    // Pânza acoperă graficul cât desenezi, deci crucea lui dispare. O desenăm noi — cu
    // prețul pe axa din dreapta și ora jos — ca să vezi la ce nivel pui linia.
    if (this.cursor && (this.tool !== 'cursor' || this.mutaDesen || this.mutaCapatul)) this.#cruce(this.cursor);
  }

  #cruce(p) {
    const ctx = this.ctx;
    const dpr = window.devicePixelRatio || 1;
    const w = this.canvas.width / dpr;
    const h = this.canvas.height / dpr;
    const lw = this.chart.chart;
    let axa = 60;
    let jos = 28;
    try { axa = lw.priceScale('right').width() || 60; } catch { /* implicit */ }
    try { jos = lw.timeScale().height() || 28; } catch { /* implicit */ }
    const piata = this.snap(this.toMarket(p.x, p.y));
    ctx.save();
    ctx.strokeStyle = 'rgba(160, 170, 190, .7)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(0, p.y + 0.5); ctx.lineTo(w - axa, p.y + 0.5);
    ctx.moveTo(p.x + 0.5, 0); ctx.lineTo(p.x + 0.5, h - jos);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.font = "11px 'Inter', sans-serif";
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    if (piata) {
      // prețul, pe axa din dreapta
      const text = fmtPrice(piata.price, priceDigits(piata.price));
      ctx.fillStyle = '#2B3245';
      ctx.fillRect(w - axa, p.y - 9, axa, 18);
      ctx.fillStyle = '#E6EDF3';
      ctx.fillText(text, w - axa / 2, p.y);
      // ora, pe axa de jos
      const t = fmtTimpCruce(piata.time);
      const lat = ctx.measureText(t).width + 14;
      const x0 = Math.max(0, Math.min(w - axa - lat, p.x - lat / 2));
      ctx.fillStyle = '#2B3245';
      ctx.fillRect(x0, h - jos + 1, lat, jos - 2);
      ctx.fillStyle = '#E6EDF3';
      ctx.fillText(t, x0 + lat / 2, h - jos / 2);
    }
    ctx.restore();
  }

  /** Mânerele albe de la capete plus clopoțelul de alertă, ca la ScalpX. */
  #drawSelection(drawing) {
    const ctx = this.ctx;
    const pts = drawing.points.map((p) => this.toPixel(p)).filter(Boolean);
    if (!pts.length) return;

    ctx.save();
    for (const p of pts) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, HANDLE_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = 'rgba(11, 14, 22, .85)';
      ctx.stroke();
    }

    const bell = this.#bellCenter(drawing);
    if (bell) {
      const armed = !!drawing.alertId;
      ctx.beginPath();
      ctx.arc(bell.x, bell.y, BELL_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = armed ? 'rgba(240,185,11,.22)' : 'rgba(11,14,22,.85)';
      ctx.fill();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = armed ? '#F0B90B' : '#ffffff';
      ctx.stroke();

      ctx.fillStyle = armed ? '#F0B90B' : '#ffffff';
      ctx.font = '900 10px "Font Awesome 6 Free"';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('', bell.x, bell.y + 0.5);   // fa-bell
    }
    ctx.restore();
  }

  /** Desenul în curs, cu punctul curent al mouse-ului ca ultim vârf. */
  #withCursor(pending) {
    if (!this.cursor) return pending;
    const live = this.snap(this.toMarket(this.cursor.x, this.cursor.y));
    if (!live) return pending;
    const def = TOOL_BY_ID.get(pending.tool);
    if (def?.points === 'free') return pending;
    return { ...pending, points: [...pending.points, live] };
  }

  #draw(d, highlighted = false, preview = false) {
    const ctx = this.ctx;
    const pts = d.points.map((p) => this.toPixel(p));
    if (pts.some((p) => p === null)) return;

    const w = this.canvas.width / (window.devicePixelRatio || 1);
    const h = this.canvas.height / (window.devicePixelRatio || 1);

    ctx.save();
    ctx.globalAlpha = (d.opacity ?? 1) * (preview ? 0.75 : 1);
    ctx.strokeStyle = d.color;
    ctx.fillStyle = d.color;
    // Cu 1 px mai subțiri decât grosimea aleasă (minimum 1): liniile groase acopereau
    // lumânările. Selecția nu le mai îngroașă — doar o umbră discretă.
    ctx.lineWidth = Math.max(1, (d.width || 2) - 1);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (highlighted) { ctx.shadowColor = d.color; ctx.shadowBlur = 3; }

    const line = (a, b) => { ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke(); };

    switch (d.tool) {
      case 'trend':
        if (pts.length >= 2) line(pts[0], pts[1]);
        break;

      case 'hline':
        line({ x: 0, y: pts[0].y }, { x: w, y: pts[0].y });
        this.#tag(pts[0].y, d.points[0].price, d.color, d.alertId);
        break;

      case 'hray':
        line(pts[0], { x: w, y: pts[0].y });
        this.#tag(pts[0].y, d.points[0].price, d.color, d.alertId);
        break;

      case 'vline':
        line({ x: pts[0].x, y: 0 }, { x: pts[0].x, y: h });
        break;

      case 'ray': {
        if (pts.length < 2) break;
        const dx = pts[1].x - pts[0].x, dy = pts[1].y - pts[0].y;
        const scale = Math.max(w, h) * 2 / (Math.hypot(dx, dy) || 1);
        line(pts[0], { x: pts[0].x + dx * scale, y: pts[0].y + dy * scale });
        break;
      }

      case 'arrow': {
        if (pts.length < 2) break;
        line(pts[0], pts[1]);
        const angle = Math.atan2(pts[1].y - pts[0].y, pts[1].x - pts[0].x);
        const size = 10 + (d.width || 2);
        ctx.beginPath();
        ctx.moveTo(pts[1].x, pts[1].y);
        ctx.lineTo(pts[1].x - size * Math.cos(angle - Math.PI / 7), pts[1].y - size * Math.sin(angle - Math.PI / 7));
        ctx.lineTo(pts[1].x - size * Math.cos(angle + Math.PI / 7), pts[1].y - size * Math.sin(angle + Math.PI / 7));
        ctx.closePath();
        ctx.fill();
        break;
      }

      case 'rect': {
        if (pts.length < 2) break;
        const x = Math.min(pts[0].x, pts[1].x), y = Math.min(pts[0].y, pts[1].y);
        const rw = Math.abs(pts[1].x - pts[0].x), rh = Math.abs(pts[1].y - pts[0].y);
        ctx.globalAlpha *= 0.15;
        ctx.fillRect(x, y, rw, rh);
        ctx.globalAlpha = (d.opacity ?? 1) * (preview ? 0.75 : 1);
        ctx.strokeRect(x, y, rw, rh);
        break;
      }

      case 'fib': {
        if (pts.length < 2) break;
        const [a, b] = d.points;
        const range = b.price - a.price;
        ctx.font = '10px Inter, sans-serif';
        for (const level of this.fibLevels) {
          const price = a.price + range * level;
          const y = this.chart.candles.priceToCoordinate(price);
          if (y === null) continue;
          ctx.globalAlpha = (d.opacity ?? 1) * 0.7;
          line({ x: Math.min(pts[0].x, pts[1].x), y }, { x: w, y });
          ctx.globalAlpha = 1;
          ctx.fillText(
            `${(level * 100).toFixed(1)}%  ${fmtPrice(price, priceDigits(price))}`,
            Math.min(pts[0].x, pts[1].x) + 4, y - 3,
          );
        }
        break;
      }

      case 'ruler': {
        if (pts.length < 2) break;
        ctx.setLineDash([4, 3]);
        line(pts[0], pts[1]);
        ctx.setLineDash([]);
        const [a, b] = d.points;
        const pct = a.price ? ((b.price - a.price) / a.price) * 100 : 0;
        const step = TF_SECONDS[this.chart.tf] || 300;
        const bars = Math.round(Math.abs(b.time - a.time) / step);
        this.#box(
          (pts[0].x + pts[1].x) / 2, (pts[0].y + pts[1].y) / 2,
          [`${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`, `${bars} баров`],
          pct >= 0 ? '#26a69a' : '#ef5350',
        );
        break;
      }

      case 'rr': {
        if (pts.length < 2) break;
        const long = (d.side || 'long') === 'long';
        const entry = d.points[0].price;
        const stop = d.points[1].price;
        const target = d.points[2]?.price ?? entry + (entry - stop) * 2;
        const x0 = pts[0].x;
        const x1 = pts[2] ? pts[2].x : Math.max(pts[0].x, pts[1].x) + 80;

        const band = (from, to, color) => {
          const y0 = this.chart.candles.priceToCoordinate(from);
          const y1 = this.chart.candles.priceToCoordinate(to);
          if (y0 === null || y1 === null) return;
          ctx.fillStyle = color;
          ctx.globalAlpha = 0.18;
          ctx.fillRect(Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0));
          ctx.globalAlpha = d.opacity ?? 1;
          ctx.strokeStyle = color;
          ctx.beginPath();
          ctx.moveTo(Math.min(x0, x1), y1); ctx.lineTo(Math.max(x0, x1), y1);
          ctx.stroke();
        };
        band(entry, stop, '#ef5350');
        band(entry, target, '#26a69a');

        const risk = Math.abs(entry - stop);
        const reward = Math.abs(target - entry);
        this.#box(x1, pts[0].y, [
          long ? 'Long' : 'Short',
          `R:R ${risk ? (reward / risk).toFixed(2) : '—'}`,
        ], long ? '#26a69a' : '#ef5350');
        break;
      }

      case 'brush':
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
        ctx.stroke();
        break;

      case 'text':
        ctx.font = `${11 + (d.width || 2) * 2}px Inter, sans-serif`;
        ctx.fillText(d.text || '', pts[0].x + 4, pts[0].y);
        break;

      default:
        break;
    }
    ctx.restore();
  }

  /**
   * Eticheta de preț de pe marginea dreaptă, pentru liniile orizontale.
   * Cu alertă activă apare și un clopoțel, ca să se vadă care nivel e armat.
   */
  #tag(y, price, color, alertId = null) {
    const ctx = this.ctx;
    const w = this.canvas.width / (window.devicePixelRatio || 1);
    const text = (alertId ? '⚑ ' : '') + fmtPrice(price, priceDigits(price));
    ctx.save();
    ctx.font = "10px Manrope, sans-serif";
    const width = ctx.measureText(text).width + 8;
    ctx.globalAlpha = 1;
    ctx.fillStyle = color;
    ctx.fillRect(w - width - 2, y - 7, width, 14);
    ctx.fillStyle = '#0B0E16';
    ctx.fillText(text, w - width + 2, y + 3);
    ctx.restore();
  }

  /** Cutie de etichete (linia/riscul), desenată lângă un punct. */
  #box(x, y, lines, color) {
    const ctx = this.ctx;
    ctx.save();
    ctx.font = '10px Inter, sans-serif';
    const width = Math.max(...lines.map((t) => ctx.measureText(t).width)) + 10;
    const height = lines.length * 13 + 6;
    ctx.globalAlpha = 0.92;
    ctx.fillStyle = '#141c2b';
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.fillRect(x + 6, y - height / 2, width, height);
    ctx.strokeRect(x + 6, y - height / 2, width, height);
    ctx.fillStyle = color;
    ctx.globalAlpha = 1;
    lines.forEach((text, i) => ctx.fillText(text, x + 11, y - height / 2 + 14 + i * 13));
    ctx.restore();
  }

  destroy() {
    window.removeEventListener('keydown', this.keyHandler);
    window.removeEventListener('keyup', this.keyUpHandler);
    this.host.removeEventListener('pointermove', this.hostMove);
    this.host.removeEventListener('pointerdown', this.hostDown);
    this.host.removeEventListener('pointerup', this.hostUp);
    this.host.removeEventListener('dblclick', this.hostDblClick);
    this.host.style.cursor = '';
    this.resizeObserver?.disconnect();
    this.canvas.remove();
  }
}
